import { useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { components, createFilter } from 'react-select';
import CreatableSelect from 'react-select/creatable';

import CloseXs from '@/assets/icons/CloseXs';
import { Label } from '@/components/UI/Label/Label';
import { reactSelectOnBlurWorkaround } from '@/utils/helpers';

import styles from '@/components/UI/Fields/Fields.module.css';

type GroupedOption = {
  label: string;
  options: { label: string; value: string[] }[];
};

type GroupedSelectOption = { label: string; value: string | string[] };

type GroupedCreatableSelectFieldProps = {
  control: any;
  name: string;
  label: string;
  testId: string;
  placeholder: string;
  defaultValue?: { label: string; value: string }[];
  options: GroupedOption[];
  filterMatchFrom?: 'start' | 'any';
  formatCreateLabel: (input: string) => string;
  getExactMatchLabel?: (optionLabel: string) => string;
  normalizeCreatedValue?: (value: string) => string;
  isOptionSelected?: (option: GroupedSelectOption, selectedValues: string[]) => boolean;
  marginBottom?: number;
};

const createOption = (value: string) => ({ label: value, value });

const defaultIsOptionSelected = (option: GroupedSelectOption, selectedValues: string[]) => {
  const optionValues = Array.isArray(option.value) ? option.value : [option.value];
  return optionValues.length > 0 && optionValues.every((value) => selectedValues.includes(value));
};

/**
 * Normalizes a raw field value (which may be an array of plain strings, or an array of
 * `{ label, value }` option objects where `value` is either a string or a string[])
 * into a flat array of plain strings. This is the single format react-hook-form's
 * field state should ever hold for this component, both on initial mount (defaultValue)
 * and after user interaction (onChange).
 */
const normalizeToStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.flatMap((v: any) => {
        if (typeof v === 'string') return v;
        if (Array.isArray(v.value)) return v.value;
        return v.value ?? [];
      })
    : [];

const MultiValue = (props: any) => <components.MultiValue {...props}>{props.data.label}</components.MultiValue>;

const CustomMultiValueRemove = (props: any) => (
  <div style={{ display: 'flex' }} {...props.innerProps}>
    <CloseXs fill='currentColor' />
  </div>
);

const moveExactMatchesToTop = (
  groups: GroupedOption[],
  normalizedInput: string,
  getExactMatchLabel: (optionLabel: string) => string,
): GroupedOption[] => {
  const isExactMatch = (optionLabel: string) => getExactMatchLabel(optionLabel).toLocaleLowerCase() === normalizedInput;

  // Puts exact matches (true) before non-matches (false).
  const exactMatchesFirst = (aIsExact: boolean, bIsExact: boolean) => Number(bIsExact) - Number(aIsExact);

  const rankedGroups = groups.map((group) => {
    const sortedOptions = group.options
      .map((option) => ({ ...option }))
      .sort((a, b) => exactMatchesFirst(isExactMatch(a.label), isExactMatch(b.label)));

    return { ...group, options: sortedOptions };
  });

  const groupHasExactMatch = (group: GroupedOption) => group.options.some((option) => isExactMatch(option.label));

  return rankedGroups.sort((a, b) => exactMatchesFirst(groupHasExactMatch(a), groupHasExactMatch(b)));
};

export default function GroupedCreatableSelectField({
  control,
  name,
  label,
  testId,
  placeholder,
  defaultValue = [],
  options,
  filterMatchFrom = 'any',
  formatCreateLabel,
  getExactMatchLabel,
  normalizeCreatedValue = (v) => v,
  isOptionSelected = defaultIsOptionSelected,
  marginBottom = 0,
}: GroupedCreatableSelectFieldProps) {
  const filterConfig = { matchFrom: filterMatchFrom };
  const [inputValue, setInputValue] = useState('');

  const normalizedInput = inputValue.trim().toLocaleLowerCase();
  const prioritizedOptions = useMemo(() => {
    if (!getExactMatchLabel || !normalizedInput) return options;
    return moveExactMatchesToTop(options, normalizedInput, getExactMatchLabel);
  }, [options, normalizedInput, getExactMatchLabel]);

  return (
    <div style={marginBottom ? { marginBottom: `${marginBottom}px` } : undefined}>
      <Label htmlFor={`create-select-${testId}`} aria-label={label}>
        {label}
      </Label>
      <div className={styles.selectContainer}>
        <Controller
          control={control}
          name={name}
          defaultValue={normalizeToStringArray(defaultValue)}
          render={({ field }) => {
            const selectedValues: string[] = normalizeToStringArray(field.value);

            const selectedOptions = selectedValues.map(createOption);

            const handleChange = (newValue: any) => {
              if (!newValue || newValue.length === 0) {
                field.onChange([]);
                return;
              }

              const expanded = new Set<string>(selectedValues);

              // Process the last added option
              const lastOption = newValue[newValue.length - 1];
              if (lastOption) {
                const val = lastOption.value;
                if (Array.isArray(val)) {
                  val.forEach((item: string) => expanded.add(item));
                } else {
                  expanded.add(normalizeCreatedValue(val));
                }
              }

              // Handle removals
              const newValueStrings = new Set(
                newValue.map((v: any) => (typeof v.value === 'string' ? v.value : null)).filter(Boolean),
              );
              for (const prev of selectedValues) {
                if (!newValueStrings.has(prev)) {
                  expanded.delete(prev);
                }
              }

              field.onChange(Array.from(expanded));
            };

            return (
              <CreatableSelect
                id={`create-select-${testId}`}
                inputId={testId}
                formatCreateLabel={formatCreateLabel}
                placeholder={placeholder}
                className='react-select-container'
                classNamePrefix='react-select'
                noOptionsMessage={() => 'Geen opties'}
                options={prioritizedOptions as any}
                value={selectedOptions}
                onInputChange={setInputValue}
                onBlur={reactSelectOnBlurWorkaround}
                filterOption={createFilter(filterConfig)}
                onChange={handleChange}
                isOptionSelected={(option: GroupedSelectOption) => isOptionSelected(option, selectedValues)}
                isMulti
                isClearable
                isSearchable
                components={{
                  MultiValueRemove: CustomMultiValueRemove,
                  MultiValue,
                }}
                styles={{
                  valueContainer: (base) => ({
                    ...base,
                    maxHeight: '100px',
                    overflowY: 'auto',
                  }),
                }}
              />
            );
          }}
        />
      </div>
    </div>
  );
}
